import classNames from 'classnames';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import type { BingoCoinType, MetagameBingoEvent } from '../../stores/bingo/types';

import { BannerResult } from './BannerResult';
import { CountdownTimer } from './CountdownTimer';
import styles from './LobbyPassBanner.module.scss';
import type { BannerConfig } from './useBannerConfig';

interface RotationData {
  displayEvent: MetagameBingoEvent | null;
  shouldRotate: boolean;
  upcomingEvents: MetagameBingoEvent[];
}

interface BannerTitleProps {
  config: BannerConfig;
  hasBingoResultToShow: boolean;
  bingoResultToShow?: { coinType: BingoCoinType; totalUserReward: number; gameId: string };
  rotationData: RotationData;
  showCheckersBackground: boolean;
}

export const BannerTitle = ({
  config,
  hasBingoResultToShow,
  bingoResultToShow,
  rotationData,
  showCheckersBackground,
}: BannerTitleProps) => {
  const { t } = useTranslation();
  const { displayEvent: rotatingEvent, shouldRotate } = rotationData;

  const displayEvent = shouldRotate && rotatingEvent ? rotatingEvent : config.eventToDisplay;

  const TIMER_BUFFER_MS = 3000;
  const bufferedStartMs = displayEvent?.startDate ? new Date(displayEvent.startDate).getTime() + TIMER_BUFFER_MS : null;
  const timeToStartInMs = bufferedStartMs ? dayjs(bufferedStartMs).diff(dayjs(), 'milliseconds') : 0;
  const isUpcomingEvent = bufferedStartMs ? dayjs(bufferedStartMs).isAfter(dayjs()) : false;

  const hasReward = Boolean(bingoResultToShow?.totalUserReward);
  const hasResultWithReward = hasBingoResultToShow && bingoResultToShow && hasReward;

  const renderTitleContent = () => {
    if (hasResultWithReward) {
      return <BannerResult bingoResultToShow={bingoResultToShow} titleText={config.titleText} />;
    }

    if (isUpcomingEvent) {
      return (
        <span className={styles.titleText}>
          {t('prime.lobbyBanner.willStartIn')}{' '}
          <CountdownTimer key={displayEvent?.startDate} startsInMs={timeToStartInMs} />
        </span>
      );
    }

    return <span className={styles.titleText}>{config.titleText}</span>;
  };

  if (!bingoResultToShow?.coinType && !displayEvent?.coinType) {
    return null;
  }

  const eventType = hasReward ? bingoResultToShow?.coinType : displayEvent?.coinType;
  const eventTypeText = eventType === 'GC' ? 'CC' : 'Sweeps';

  const eventTypeClass = showCheckersBackground ? null : eventType === 'GC' ? styles.bannerGC : styles.bannerCASH;

  const animationKey = shouldRotate ? `${displayEvent?.coinType}-${displayEvent?.startDate}` : 'static';

  return (
    <div
      key={animationKey}
      className={classNames(styles.titleContainer, eventTypeClass, {
        [styles.animateIn]: shouldRotate,
      })}
    >
      {!hasResultWithReward && (
        <span className={styles.bingoType} data-testid="banner-bingo-type">
          {`${eventTypeText} `}
          {t('prime.lobbyBanner.bingo')}
        </span>
      )}
      {renderTitleContent()}
    </div>
  );
};
